/**
 * Auth service client — verified against the Auth service source.
 *
 *  - POST /auth/specialist/login { identifier, password }  (username OR email)
 *    → Keycloak token set · 403 "PASSWORD_CHANGE_REQUIRED: …" when the
 *    account still carries a clinic-issued temporary password (ZOY-283)
 *  - POST /auth/specialist/password/complete { identifier, currentPassword,
 *    newPassword } → token set (first-login completion; ZOY-283)
 *  - POST /auth/specialist/otp/request { phone } → uniform ack
 *    (anti-enumeration); 429 carries retryAfterSeconds (+ Retry-After header)
 *  - POST /auth/specialist/otp/verify { phone, code } → token set stamped
 *    apc=specialist; invalid/expired/locked are indistinguishable 401s BY
 *    DESIGN
 *
 *    SPECIALIST, never STAFF (ZOY-524): `staff` is Zoyaar platform staff and
 *    `specialist` is clinic personnel — deliberately different authorities,
 *    on disjoint OTP purposes, so one human may hold both and a code minted
 *    for one surface is rejected by the other. The staff surface is
 *    login-only for staff accounts, so a clinic specialist's number issued no
 *    code there at all: the mobile sign-in simply never arrived.
 *  - POST /auth/password-recovery/start { email } → uniform OTP ack; the code
 *    goes to the account's registered mobile (ZOY-284)
 *  - POST /auth/password-recovery/verify { email, code } → { recoveryToken }
 *  - POST /auth/password-recovery/complete { recoveryToken, newPassword }
 *    → { uid, sessionsRevoked } — every session is revoked
 *  - GET  /identity/me → the caller's Auth profile (uid, username, displayName,
 *    firstName, lastName, email); the ONE source of a human's name
 *  - POST /identity/me/password { currentPassword, newPassword } (signed in)
 *    → { uid, currentSessionRetained, otherSessionsRevoked } (ZOY-286)
 *  - POST /auth/token/refresh { refresh_token } · POST /auth/logout (204)
 *
 * Auth errors are Nest-style: the code is the "CODE: text" prefix of
 * `message` (see ApiError.authCode).
 */

import { apiRequest } from '../api/http';
import { clearSession, getStoredRefreshToken, storeTokenSet, type TokenSet } from './tokens';

export async function loginWithPassword(identifier: string, password: string): Promise<void> {
  const tokens = await apiRequest<TokenSet>({
    base: 'auth',
    method: 'POST',
    path: '/auth/specialist/login',
    body: { identifier, password },
    auth: false,
  });
  storeTokenSet(tokens);
}

export interface OtpRequestAck {
  /** Uniform acknowledgement — identical whether or not an account exists. */
  message: string;
  expiresInSeconds: number;
  resendCooldownSeconds: number;
  /**
   * DEVELOPMENT/STAGING ONLY (Auth: OTP_DEV_CODE_ENABLED + NODE_ENV
   * development|test). Production never returns it. The frontend only ever
   * DISPLAYS it — see features/access/DevOtpNotice.tsx.
   */
  devCode?: string;
}

export async function otpRequest(phone: string): Promise<OtpRequestAck> {
  return apiRequest<OtpRequestAck>({
    base: 'auth',
    method: 'POST',
    path: '/auth/specialist/otp/request',
    body: { phone },
    auth: false,
  });
}

export async function otpVerify(phone: string, code: string): Promise<void> {
  const tokens = await apiRequest<TokenSet>({
    base: 'auth',
    method: 'POST',
    path: '/auth/specialist/otp/verify',
    body: { phone, code },
    auth: false,
  });
  storeTokenSet(tokens);
}

/** Revokes the Keycloak session (idempotent) and clears local material. */
export async function logout(): Promise<void> {
  const refresh = getStoredRefreshToken();
  if (refresh) {
    try {
      await apiRequest<void>({
        base: 'auth',
        method: 'POST',
        path: '/auth/logout',
        body: { refresh_token: refresh },
        auth: false,
      });
    } catch {
      /* revocation is best-effort; local clear always happens */
    }
  }
  clearSession();
}

/* ------------------------------------------------------------------ */
/* Password lifecycle (Auth ZOY-283 / ZOY-284 / ZOY-286)               */
/* ------------------------------------------------------------------ */

export const AuthCode = {
  /** 403 on login: a temporary password must be replaced before a session exists. */
  PasswordChangeRequired: 'PASSWORD_CHANGE_REQUIRED',
  /** 409 on completion: the account no longer carries a temporary password. */
  PasswordChangeNotRequired: 'PASSWORD_CHANGE_NOT_REQUIRED',
  PasswordUnchanged: 'PASSWORD_UNCHANGED',
  PasswordPolicyRejected: 'PASSWORD_POLICY_REJECTED',
  CurrentPasswordIncorrect: 'CURRENT_PASSWORD_INCORRECT',
  PasswordNotApplicable: 'PASSWORD_NOT_APPLICABLE',
  PasswordChangeUnavailable: 'PASSWORD_CHANGE_UNAVAILABLE',
  RecoveryInvalid: 'RECOVERY_INVALID',
  WrongLoginSurface: 'WRONG_LOGIN_SURFACE',
} as const;

/**
 * First-login completion (ZOY-283): replaces the temporary password and
 * returns a normal session in one step. Distinct from recovery — the person
 * KNOWS the temporary password.
 */
export async function completeTemporaryPassword(args: {
  identifier: string;
  currentPassword: string;
  newPassword: string;
}): Promise<void> {
  const tokens = await apiRequest<TokenSet>({
    base: 'auth',
    method: 'POST',
    path: '/auth/specialist/password/complete',
    body: args,
    auth: false,
  });
  storeTokenSet(tokens);
}

/** RECOVERY step 1 — identify by email; the code is texted to the registered mobile. */
export async function startPasswordRecovery(email: string): Promise<OtpRequestAck> {
  return apiRequest<OtpRequestAck>({
    base: 'auth',
    method: 'POST',
    path: '/auth/password-recovery/start',
    body: { email },
    auth: false,
  });
}

export interface PasswordRecoveryVerified {
  recoveryToken: string;
  expiresInSeconds: number;
}

/** RECOVERY step 2 — verify the code; yields the short-lived recovery token. */
export async function verifyPasswordRecovery(
  email: string,
  code: string,
): Promise<PasswordRecoveryVerified> {
  return apiRequest<PasswordRecoveryVerified>({
    base: 'auth',
    method: 'POST',
    path: '/auth/password-recovery/verify',
    body: { email, code },
    auth: false,
  });
}

export interface PasswordRecoveryCompleted {
  uid: string;
  sessionsRevoked: boolean;
}

/** RECOVERY step 3 — set the new password; every existing session is revoked. */
export async function completePasswordRecovery(
  recoveryToken: string,
  newPassword: string,
): Promise<PasswordRecoveryCompleted> {
  return apiRequest<PasswordRecoveryCompleted>({
    base: 'auth',
    method: 'POST',
    path: '/auth/password-recovery/complete',
    body: { recoveryToken, newPassword },
    auth: false,
  });
}

export interface PasswordChanged {
  uid: string;
  /** False means THIS session was revoked too — route back to sign-in. */
  currentSessionRetained: boolean;
  otherSessionsRevoked: boolean;
}

/** Signed-in change-own-password (ZOY-286). */
export async function changeOwnPassword(
  currentPassword: string,
  newPassword: string,
): Promise<PasswordChanged> {
  return apiRequest<PasswordChanged>({
    base: 'auth',
    method: 'POST',
    path: '/identity/me/password',
    body: { currentPassword, newPassword },
  });
}

/**
 * The signed-in human's Auth profile (GET /identity/me). Names live HERE and
 * nowhere else: the clinic backend resolves every staff attribution from
 * this same profile (displayName, else username), so the header and the
 * account page read the identical source rather than a copy.
 */
export interface MyProfile {
  uid: string;
  username: string | null;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

export function fetchMyProfile(): Promise<MyProfile> {
  return apiRequest<MyProfile>({ base: 'auth', path: '/identity/me' });
}
